import type { ListWelloPolicyCodeAllDto } from '__generated__/api/unauth';
import {
  BottomDrawer,
  Button,
  Input,
  Skeleton,
  Spinner,
} from '@common/components';
import { useToast } from '@common/hooks';
import { SvgCheck, SvgChevrondown, SvgTarget2 } from '@common/icon';
import { cn, CustomError, isEnumValue } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';

import {
  EXCEPTED_CODE,
  EXCEPTED_HIDDEN_CODE,
  UnauthQueryOptions,
  WELLO_POLICY_CODE_TYPE,
} from '@/api';
import type { SEARCH_PARAMS_TYPE } from '@/constants';
import { SELECTOR } from '@/constants';
import type { LocationData } from '@/hooks/useLocation';
import { useLocation } from '@/hooks/useLocation';

export interface SelectedRegion {
  mainRegion: ListWelloPolicyCodeAllDto;
  subRegion: ListWelloPolicyCodeAllDto;
}

export interface SelectedLocation {
  latitude: number;
  longitude: number;
}

interface RegionSelectProps {
  value?: SEARCH_PARAMS_TYPE.SELECTED_REGION;
  onChange?: (value: SelectedRegion) => void;
  onClickGps?: (value: LocationData) => void;
  id?: string;
  className?: string;
  labelClassName?: string;
  label?: string;
  placeholder?: string;
  isLoading?: boolean;
  enableMainRegionCodeList?: string[];
  enableLocation?: boolean;
}

export const RegionSelect = ({
  value,
  onChange,
  onClickGps,
  id,
  className,
  labelClassName,
  label = '지역',
  isLoading = false,
  enableMainRegionCodeList,
  enableLocation = true,
  placeholder = '지역을 선택해 주세요.',
}: RegionSelectProps) => {
  const policyFitMetaDataQuery = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/wello-policy-code/all'](),
    staleTime: Infinity,
    gcTime: Infinity,
    select: ({ context }) => {
      const { contents } = context ?? {};

      if (!contents)
        throw new CustomError({ return_message: '정보가 없습니다.' });

      const boundedMetaData: ListWelloPolicyCodeAllDto[][] = [];

      const genderMetaData: ListWelloPolicyCodeAllDto[] = [];

      const regionMetaData: {
        mainRegion: ListWelloPolicyCodeAllDto[];
        subRegion: ListWelloPolicyCodeAllDto[];
      } = {
        mainRegion: [],
        subRegion: [],
      };

      contents.forEach((content) => {
        const { code } = content;
        let { level } = content;

        if (!code || !level) return;

        const isHidden =
          code.includes('99') && !isEnumValue(EXCEPTED_HIDDEN_CODE, code);

        if (isHidden) return;

        if (code?.includes(EXCEPTED_CODE.GENDER)) {
          if (level === 2) genderMetaData.push(content);

          return;
        }

        if (code.startsWith('C14')) level = level - 1;

        if (code?.includes(EXCEPTED_CODE.REGION)) {
          switch (level) {
            case 2:
              return regionMetaData.mainRegion.push(content);
            case 3:
              return regionMetaData.subRegion.push(content);
            default:
              return;
          }
        }

        if (level === 3 && code.startsWith(EXCEPTED_CODE.UNEMPLOYMENT)) return;

        if (level === 1) return boundedMetaData.push([content]);

        const rootMetaData = boundedMetaData.find(([rootMetaData]) => {
          const rootMetaDataCode = rootMetaData.code;

          return rootMetaDataCode ? code.includes(rootMetaDataCode) : false;
        });

        return rootMetaData?.push(content);
      });

      const boundedCustomPolicyMetaData: ListWelloPolicyCodeAllDto[][] = [];
      const boundedInterestMetaData = boundedMetaData.filter((metaDataList) => {
        const [rootMetaData] = metaDataList;

        if (
          rootMetaData.welloPolicyCodeTypeCd === WELLO_POLICY_CODE_TYPE.INTEREST
        ) {
          return true;
        } else {
          //! 관심 필터는 원래 없어서 여기서 예외처리
          if (!rootMetaData.welloPolicyCodeFilterCd) return false;

          boundedCustomPolicyMetaData.push(metaDataList);

          return false;
        }
      });

      return {
        customPolicyMetaDataList: boundedCustomPolicyMetaData.flat(),
        interestedCategoryMetaDataList: boundedInterestMetaData.flat(),
        genderMetaData,
        regionMetaData,
      };
    },
  });

  const { regionMetaData } = policyFitMetaDataQuery.data ?? {};

  const { refetch: getLocation, isLoading: isLocationLoading } = useLocation();

  const { toast } = useToast();

  const [selectedValue, setSelectedValue] =
    useState<SEARCH_PARAMS_TYPE.SELECTED_REGION>();

  const mainRegionList = regionMetaData?.mainRegion.filter((mainRegion) => {
    if (!enableMainRegionCodeList) return true;

    return enableMainRegionCodeList.includes(mainRegion.code ?? '');
  });

  const mainRegion = mainRegionList?.find(
    ({ code }) => code === value?.mainRegionCode,
  );

  const subRegion = regionMetaData?.subRegion.find(
    ({ code }) => code === value?.subRegionCode,
  );

  const selectedRegionText =
    mainRegion && subRegion ? `${mainRegion.value} > ${subRegion.value}` : '';

  const inputId = useId();

  const isPending =
    isLoading || isLocationLoading || policyFitMetaDataQuery.isLoading;

  return (
    <div className={className}>
      <div className="mb-10 flex w-full items-center justify-between gap-4 text-Gray-800 typo-body4">
        <label className={labelClassName} htmlFor={inputId} id={id}>
          {label}
        </label>
        {enableLocation ? (
          isPending ? (
            <Skeleton className="h-21 w-55" />
          ) : (
            <button
              className="flex items-center gap-5 font-400 text-Gray-900 typo-body4"
              type="button"
              onClick={async () => {
                const { data: location } = await getLocation();

                const { codeRegion, codeSubRegion } = location ?? {};

                const mainRegion = mainRegionList?.find(
                  ({ code }) => code === codeRegion,
                );

                const subRegion = regionMetaData?.subRegion.find(
                  ({ code }) => code === codeSubRegion,
                );

                if (!mainRegion || !subRegion)
                  throw new CustomError({
                    return_message: '위치 정보를 가져오는데 실패했습니다.',
                  });

                onChange?.({
                  mainRegion,
                  subRegion,
                });

                toast({
                  message: '현재 위치가 설정되었어요.',
                });

                if (!onClickGps) return;
                if (onClickGps) {
                  if (!location)
                    throw new CustomError({
                      return_message: '위치 정보를 가져오는데 실패했습니다.',
                    });

                  onClickGps(location);
                }
              }}
            >
              {isLocationLoading ? (
                <Spinner size={14} />
              ) : (
                <SvgTarget2 className="size-14" name="위치 정보 업데이트" />
              )}
              현위치
            </button>
          )
        ) : null}
      </div>
      <Input
        readOnly
        className="w-full"
        id={inputId}
        placeholder={placeholder}
        rightContent={
          isPending ? (
            <Spinner size={20} />
          ) : (
            <SvgChevrondown className="size-20 text-Gray-700" name="열기" />
          )
        }
        type="button"
        value={selectedRegionText}
        onClick={() => {
          if (isPending) return;

          setSelectedValue(
            value?.mainRegionCode
              ? value
              : {
                  mainRegionCode: 'C01-01',
                },
          );
        }}
      />
      <BottomDrawer
        opened={!!selectedValue}
        renderTo={() => document.getElementById(SELECTOR.MAIN_WRAPPER)}
        onClose={() => {
          setSelectedValue(undefined);
        }}
      >
        <BottomDrawer.HandleWrapper>
          <BottomDrawer.Handle />
        </BottomDrawer.HandleWrapper>
        <BottomDrawer.Contents className="overflow-hidden">
          <h6 className="mb-20 px-10 font-700 typo-body1">
            관심 지역을 선택해 주세요
          </h6>
          <section className="flex max-h-300">
            <ul className="flex-1 overflow-auto border-r-1 border-Gray-300 px-5 hide-scrollbar webkit-scroll-touch">
              {mainRegionList?.map(({ value, code }) => {
                if (!code || !value) return null;

                const isSelected = code === selectedValue?.mainRegionCode;

                return (
                  <li key={code}>
                    <button
                      type="button"
                      className={cn(
                        'flex w-full items-center justify-between text-nowrap px-12 py-8 font-400 text-Gray-800 typo-body3',
                        {
                          'font-600 text-B-500': isSelected,
                        },
                      )}
                      onClick={() => {
                        if (code === selectedValue?.mainRegionCode) return;

                        setSelectedValue({
                          mainRegionCode: code,
                        });
                      }}
                      aria-pressed={isSelected}
                    >
                      {value}
                      {isSelected ? (
                        <SvgCheck
                          className="size-16 stroke-B-500 text-B-500"
                          name="Check"
                          strokeWidth={1}
                        />
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
            <ul className="flex-1 overflow-auto px-5 hide-scrollbar webkit-scroll-touch">
              {regionMetaData?.subRegion
                .filter(
                  ({ parentCode: parent_code }) =>
                    parent_code === selectedValue?.mainRegionCode,
                )
                .sort((a, b) => {
                  if (
                    typeof a.value !== 'string' ||
                    typeof b.value !== 'string'
                  ) {
                    return 0;
                  }

                  return a?.value.localeCompare(b?.value);
                })
                .map(({ code, value }) => {
                  if (!code || !value) return null;

                  const isSelected = code === selectedValue?.subRegionCode;

                  return (
                    <li key={code}>
                      <button
                        type="button"
                        className={cn(
                          'flex w-full items-center justify-between text-nowrap px-12 py-8 font-400 text-Gray-800 duration-300 typo-body3 animate-in slide-in-from-top',
                          {
                            'font-600 text-B-500': isSelected,
                          },
                        )}
                        onClick={() => {
                          if (code === selectedValue?.subRegionCode) return;

                          setSelectedValue((prev) => ({
                            ...prev,
                            subRegionCode: code,
                          }));
                        }}
                        aria-pressed={isSelected}
                      >
                        {value}
                        {isSelected ? (
                          <SvgCheck
                            className="size-16 stroke-B-500 text-B-500"
                            name="Check"
                            strokeWidth={1}
                          />
                        ) : null}
                      </button>
                    </li>
                  );
                })}
            </ul>
          </section>
          <footer className="mt-16 w-full px-20 py-16">
            <Button
              className="w-full"
              disabled={
                !selectedValue?.mainRegionCode || !selectedValue?.subRegionCode
              }
              onClick={() => {
                const mainRegion = mainRegionList?.find(
                  ({ code }) => code === selectedValue?.mainRegionCode,
                );

                const subRegion = regionMetaData?.subRegion.find(
                  ({ code }) => code === selectedValue?.subRegionCode,
                );

                if (!mainRegion || !subRegion)
                  throw new CustomError({
                    return_message: '지역 정보를 가져오지 못했어요.',
                  });

                onChange?.({
                  mainRegion,
                  subRegion,
                });

                setSelectedValue(undefined);
              }}
            >
              선택 완료
            </Button>
          </footer>
        </BottomDrawer.Contents>
      </BottomDrawer>
    </div>
  );
};
